import { TextField as MuiTextField } from '@mui/material';
import { useId } from 'react';
import { componentSizes } from '../../theme/componentSizes';
import { r, s } from '../../theme/tokens';
import { Box } from './Box';
import { formFieldLabelSx } from './formFieldStyles';
import {
  formFieldPlaceholderTypographySx,
  formFieldTypographySx,
  resolveFormFieldBackgroundColor,
  type FormFieldStyleVariant,
} from './formFieldVariants';
import { FormHelperText } from './FormHelperText';
import { Text } from './Text';
import type { SpacingToken } from '../../theme/tokens';
import type { TextFieldProps as MuiTextFieldProps } from '@mui/material/TextField';
import type { ReactElement, ReactNode } from 'react';

export type TextFieldProps = Omit<MuiTextFieldProps, 'label' | 'helperText'> & {
  label?: ReactNode;
  helperText?: ReactNode;
  helpText?: ReactNode;
  errorText?: ReactNode;
  inputHeight?: number;
  fieldStyle?: FormFieldStyleVariant;
  inputBackgroundColor?: 'background.paper' | 'background.default';
  labelGap?: SpacingToken;
};

export const TextField = ({
  id,
  label,
  helperText,
  helpText,
  errorText,
  fullWidth = true,
  size = 'small',
  inputHeight,
  fieldStyle = 'default',
  inputBackgroundColor,
  labelGap = 'xs',
  sx,
  error,
  required,
  ...rest
}: TextFieldProps): ReactElement => {
  const isMultiline = rest.multiline === true;
  const resolvedInputHeight = inputHeight ?? componentSizes.auth.inputHeight;
  const resolvedInputBackgroundColor = resolveFormFieldBackgroundColor(
    fieldStyle,
    inputBackgroundColor,
  );
  const resolvedHelpText = helpText ?? (error ? undefined : helperText);
  const resolvedErrorText = errorText ?? (error ? helperText : undefined);
  const resolvedHelperText = resolvedErrorText ?? resolvedHelpText;
  const showError = error ? true : Boolean(resolvedErrorText);
  const fallbackId = useId();
  const resolvedId = id ?? fallbackId;
  const helperId = resolvedHelperText ? `${resolvedId}-helper` : undefined;
  const { 'aria-describedby': ariaDescribedBy, ...restProps } = rest;
  const resolvedDescribedBy =
    helperId && ariaDescribedBy ? `${ariaDescribedBy} ${helperId}` : (ariaDescribedBy ?? helperId);
  const baseSx: MuiTextFieldProps['sx'] = (theme) => ({
    '& .MuiInputBase-root': {
      backgroundColor: resolvedInputBackgroundColor,
      minHeight: `${resolvedInputHeight}px !important`,
      ...(isMultiline
        ? {
            height: 'auto !important',
            maxHeight: 'none !important',
            alignItems: 'flex-start',
          }
        : {
            height: `${resolvedInputHeight}px !important`,
            maxHeight: `${resolvedInputHeight}px !important`,
          }),
      boxSizing: 'border-box',
      ...formFieldTypographySx,
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: r('md'),
      minHeight: `${resolvedInputHeight}px !important`,
      ...(isMultiline
        ? {
            height: 'auto !important',
            alignItems: 'flex-start',
            padding: '0 !important',
          }
        : {
            height: `${resolvedInputHeight}px !important`,
          }),
      backgroundColor: resolvedInputBackgroundColor,
      ...formFieldTypographySx,
    },
    '& .MuiOutlinedInput-root.MuiInputBase-multiline': {
      padding: '0 !important',
    },
    '& .MuiOutlinedInput-input': {
      ...(isMultiline ? { height: 'auto' } : { height: '100%' }),
      boxSizing: 'border-box',
      paddingTop: isMultiline ? s('none') : s('none'),
      paddingBottom: isMultiline ? s('none') : s('none'),
      paddingLeft: s('lg'),
      paddingRight: s('md'),
      fontSize: theme.typography.subtitle1.fontSize,
      lineHeight: theme.typography.subtitle1.lineHeight,
    },
    '& .MuiInputBase-inputMultiline': {
      paddingTop: s('md'),
      paddingBottom: s('md'),
      paddingLeft: s('lg'),
      paddingRight: s('md'),
      margin: 0,
      textIndent: 0,
      boxSizing: 'border-box',
    },
    '& .MuiInputBase-input::placeholder, & .MuiOutlinedInput-input::placeholder, & input::placeholder':
      {
        ...formFieldPlaceholderTypographySx,
      },
    '& input, & textarea': {
      ...formFieldTypographySx,
    },
  });
  const mergedSx = [baseSx, ...(sx ? (Array.isArray(sx) ? sx : [sx]) : [])].filter(
    Boolean,
  ) as MuiTextFieldProps['sx'];

  return (
    <Box sx={{ display: 'grid' }}>
      {label ? (
        <Box sx={{ mb: s(labelGap) }}>
          <Text role="body" component="label" htmlFor={resolvedId} sx={formFieldLabelSx}>
            {label}
          </Text>
        </Box>
      ) : null}
      <MuiTextField
        {...restProps}
        id={resolvedId}
        label={undefined}
        error={showError}
        required={required}
        fullWidth={fullWidth}
        size={size}
        variant="outlined"
        helperText={undefined}
        aria-describedby={resolvedDescribedBy}
        sx={mergedSx}
      />
      {resolvedHelperText ? (
        <FormHelperText id={helperId} error={showError} sx={{ mt: s('xs') }}>
          {resolvedHelperText}
        </FormHelperText>
      ) : null}
    </Box>
  );
};
